"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.createLoanProductMechanics = void 0;
const _ = __importStar(require("lodash"));
const common_1 = require("@finley-technologies/common");
const formula_engine_1 = require("@finley-technologies/formula-engine");
const eligibilityEvaluator_1 = require("./eligibilityEvaluator");
const progressHelper_1 = require("./progressHelper");
const purchasePercentageRateEvaluator_1 = require("./purchasePercentageRateEvaluator");
const advanceRateEvaluator_1 = require("./receivableAdvanceRate/advanceRateEvaluator");
const RequiredIntermediateFields_1 = require("./RequiredIntermediateFields");
const valuationRateEvaluator_1 = require("./valuationRateEvaluator");
// create an interface to evaluate loanProductMechanics.
const createLoanProductMechanics = ({ config, loanTapeMetadata, f1EngineMode, }) => {
    const configs = {
        advanceRate: config.receivableAdvanceRateConfig,
    };
    // returns an array of fields that are known to be strings.
    const getStringFieldSet = () => {
        const stringFields = new Set();
        config._config_.baseFieldConfigs.forEach((baseFieldConfig) => {
            if (baseFieldConfig.type === 'string' ||
                baseFieldConfig.type === 'optionalString') {
                stringFields.add(`baseFields.${baseFieldConfig.field}`);
            }
        });
        config._config_.intermediateCalcConfigs.forEach((intermediateCalcConfig) => {
            if (intermediateCalcConfig.fieldType === 'string' ||
                intermediateCalcConfig.fieldType === 'optionalString') {
                if ((0, RequiredIntermediateFields_1.isRequiredIntermediateFieldName)(intermediateCalcConfig.field)) {
                    stringFields.add(`requiredIntermediateFields.${intermediateCalcConfig.field}`);
                }
                else {
                    stringFields.add(`dynamicIntermediateFields.${intermediateCalcConfig.field}`);
                }
            }
        });
        return stringFields;
    };
    const eligibilityEvaluator = (0, eligibilityEvaluator_1.createEligibilityEvaluator)(config.eligibilityTestConfigs, f1EngineMode);
    const valuationEvaluator = (0, valuationRateEvaluator_1.createReceivableValuationRateEvaluator)(config.receivableValuationRateConfig, f1EngineMode);
    const purchasePercentageRateEvaluator = (0, purchasePercentageRateEvaluator_1.createPurchasePercentageRateEvaluator)(config.purchasePercentageRateConfig, f1EngineMode);
    const advanceRateEvaluator = (0, advanceRateEvaluator_1.createReceivableAdvanceRateEvaluator)(config.receivableAdvanceRateConfig, getStringFieldSet(), f1EngineMode);
    const evaluateAdvanceRate = async (params) => {
        const { targetReceivables, calculationReceivables } = params;
        const { updateProgress } = (0, progressHelper_1.createProgressHelper)({
            progressCallback: params.progressCallback,
            operation: 'Compute receivable advance rates',
            numRows: targetReceivables.length,
        });
        advanceRateEvaluator.initializeEngine(calculationReceivables);
        const receivableIdToAdvanceRate = {};
        // Logic below only applies to `Formula` Configs not `Tree` Configs!
        // If there aren't any non-range variables (receivable specific) being used in the formula
        // then can avoid re-calculating the formula for every single receivable since it'll
        // always be the same (e.g., =0.875, =50+100, =0.75+SUMPRODUCT(abc_RANGE, xyz_RANGE))
        let canPerformSingleCalculation = false;
        if (config.receivableAdvanceRateConfig.type === 'formula') {
            const { nonRangeVariables } = (0, formula_engine_1.getFormulaComposition)(config.receivableAdvanceRateConfig.config.formula);
            canPerformSingleCalculation = nonRangeVariables.length === 0;
        }
        let advanceRateResult = canPerformSingleCalculation
            ? advanceRateEvaluator.evaluateConfig()
            : null;
        let progressIndex = 0;
        for (const receivable of targetReceivables) {
            // eslint-disable-next-line no-await-in-loop, no-plusplus
            await updateProgress(progressIndex++);
            if (!canPerformSingleCalculation) {
                advanceRateResult = advanceRateEvaluator.evaluateConfig(receivable);
            }
            (0, common_1.finleyAssert)(advanceRateResult !== null, 'Failed to properly retrieve advance rate as it was never calculated');
            receivableIdToAdvanceRate[receivable.finleyReceivableId] = advanceRateResult;
        }
        return receivableIdToAdvanceRate;
    };
    const evaluateValuationRate = (creditFacilityReceivable) => {
        const receivableValuationRate = valuationEvaluator.evaluateConfig(creditFacilityReceivable);
        const receivableValuation = creditFacilityReceivable.requiredIntermediateFields.exposure *
            receivableValuationRate;
        return {
            valuation: receivableValuation,
            valuationRate: receivableValuationRate,
        };
    };
    const evaluatePurchasePercentageRate = (preassignedInternalReceivable) => {
        const purchasePercentageRate = purchasePercentageRateEvaluator.evaluateConfig(preassignedInternalReceivable);
        const purchasePrice = preassignedInternalReceivable.requiredIntermediateFields.exposure *
            purchasePercentageRate;
        return {
            purchasePrice,
            purchasePercentageRate,
        };
    };
    const evaluateEligibility = ({ valuationReceivable, waivedEligibilityTestKeysForReceivable, }) => {
        const { baseFields, dynamicIntermediateFields, requiredIntermediateFields, postProcessedFields, valuationFields, assignmentFields, } = valuationReceivable;
        // pick and set the variables for the eligibility evaluator.
        eligibilityEvaluator.setVariables({
            loanTapeMetadata,
            baseFields,
            dynamicIntermediateFields,
            requiredIntermediateFields,
            postProcessedFields,
            valuationFields,
            assignmentFields,
        });
        const waiveAllEligibility = valuationReceivable.requiredIntermediateFields.waiveAllEligibility;
        const eligibilityStatuses = [];
        config.eligibilityTestConfigs.forEach((eligibilityConfig) => {
            const eligibilityStatus = eligibilityEvaluator.evaluateConfig(eligibilityConfig, {
                waiveAllEligibility,
                waivedEligibilityTestKeysForReceivable,
            });
            eligibilityStatuses.push({
                eligibilityCriteriaKey: eligibilityConfig.propertyKey,
                eligibilityStatus,
            });
        });
        // top-level eligibility determined if at least one non-waived ineligibility
        const isEligible = _.every(eligibilityStatuses, ({ eligibilityStatus }) => eligibilityStatus !== 'INELIGIBLE');
        return {
            isEligible,
            eligibilityStatuses,
        };
    };
    return {
        configs,
        evaluateEligibility,
        evaluateAdvanceRate,
        evaluateValuationRate,
        evaluatePurchasePercentageRate,
    };
};
exports.createLoanProductMechanics = createLoanProductMechanics;
//# sourceMappingURL=loanProductMechanics.js.map